import React, { useCallback, useContext, useEffect, useRef } from 'react'
import type { SearchFloatingPanelProps } from 'components/SearchV2/Components/Mobile/Panels/SearchFloatingPanel'
import SearchFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/SearchFloatingPanel'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import useDidChange from 'hooks/useDidChange'
import config from 'constants/config'
import noop from 'lib/function/noop'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useIntl } from 'react-intl'

interface Props extends
  Omit<
    SearchFloatingPanelProps,
    'title' | 'isConfirmButtonHidden' | 'children'
  >
{
  saleUnit?: string
  /** Called when user manually selects occupancy (for typeahead tracking) */
  onUserSelectedOccupancy?: (rooms: Array<App.Occupants>) => void
}

function TravellersFloatingPanel(props: Props) {
  const intl = useIntl()
  const { onConfirm = noop, saleUnit, onUserSelectedOccupancy, ...rest } = props

  const formRef = useRef<HTMLFormElement>(null)

  const { occupancies = EmptyArray, disableMultiRoom, disableSaleUnit } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  // Scroll to bottom every time a occupancy is added so the user don't
  // have to do it manually
  const didOccupanciesChanged = useDidChange(occupancies)
  const previousOccupanciesLength = useRef(occupancies.length)
  useEffect(() => {
    if (didOccupanciesChanged && occupancies.length > previousOccupanciesLength.current && formRef.current) {
      formRef.current.lastElementChild?.scrollIntoView({ block: 'end' })
    }
    previousOccupanciesLength.current = occupancies.length
  }, [didOccupanciesChanged, occupancies, formRef])

  const setOccupancies = useCallback((occupancies: Array<App.Occupants>) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies })
  }, [globalSearchDispatch])

  const onOccupanciesChange = useCallback((nextRooms: Array<App.Occupants>) => {
    setOccupancies(nextRooms)
    onUserSelectedOccupancy?.(nextRooms)

    if (!isRoomsInvalid(nextRooms)) {
      globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: false })
    }
  }, [globalSearchDispatch, setOccupancies, onUserSelectedOccupancy])

  const onApply = useCallback(() => {
    formRef.current?.requestSubmit()
  }, [formRef])

  const onSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onConfirm()
  }, [onConfirm])

  return (
    <SearchFloatingPanel
      {...rest}
      title={intl.formatMessage({
        id: 'eJ5ZDiG1837n8yqx4TC_',
        defaultMessage: 'Who is going?',
        description: 'Title for the travellers/guests selection floating panel',
      })}
      onConfirm={onApply}
    >
      <form ref={formRef} onSubmit={onSubmit}>
        <OccupancyPicker
          occupancies={occupancies}
          onChange={onOccupanciesChange}
          saleUnit={saleUnit}
          enableMultiRoom={disableMultiRoom ? false : config.accommodation.allowsMultiRoom}
          disableSaleUnit={disableSaleUnit}
        />
      </form>
    </SearchFloatingPanel>
  )
}

export default TravellersFloatingPanel
